import React from "react"
import { useSelector, useDispatch } from "react-redux"
import { useHistory, useLocation } from "react-router-dom"
import {
  Paper,
  Tabs,
  Tab,
  AppBar,
  Checkbox,
  FormControlLabel,
} from "@mui/material"

import { lessonTypes } from "../../../content"
import { RootState } from "../../../store/configureStore"
import {
  setIncludePrivate,
  setSelectedLessonType,
} from "../../../actions/searchActions"
import CustomTransientSlot from "../CustomTransientSlot"
import PickerContent from "./PickerContent"
import SpotAlreadyTaken from "./SpotAlreadyTaken"
import { removeTransientSlotIfNotReserved } from "../../../actions/lessonActions"
import { useCallOnce } from "../../../hooks/useCallOnce"

/**
 * This is the container for the availability picker logic.
 * It handles the layout, tab switching, etc. It does not
 * deal with searching. That is in PickerContent.
 */

interface Props {
  instructorId?: string
}

const AvailabilityPicker = ({ instructorId }: Props) => {
  const history = useHistory()
  const location = useLocation()
  const dispatch = useDispatch()
  const callOnce = useCallOnce()
  const { searchState } = useSelector((state: RootState) => state.location)
  const profile = useSelector((state: RootState) => state.firebase.profile)
  const { includePrivate, selectedLessonType } = useSelector(
    (state: RootState) => state.search,
  )
  const { transientSlotId } = useSelector((state: RootState) => state.booking)
  const [selectedTab, setSelectedTab] = React.useState<string | null>(null)

  const stateLower = searchState?.toLowerCase() ?? ""
  const availableLessonTypes = Object.values(lessonTypes).filter(
    (lessonType) =>
      stateLower !== "tx" && // special case, has its own lesson type picker
      stateLower !== "pa" && // special case, we don't want to show PA road tests
      !!lessonType.stateMeta[stateLower] &&
      lessonType.stateMeta[stateLower].doShowInPicker,
  )

  React.useEffect(() => {
    callOnce("maybeRemoveTransientSlot", () => {
      if (transientSlotId) {
        dispatch(removeTransientSlotIfNotReserved(transientSlotId))
      }
    })

    setSelectedTab(selectedLessonType.name)
  }, [dispatch, transientSlotId, callOnce, selectedLessonType.name])

  const handleTabChange = (event: React.SyntheticEvent, newValue: string) => {
    // If it doesn't change, do nothing.
    if (newValue === selectedTab) return

    const newLessonType = lessonTypes[newValue]
    dispatch(setSelectedLessonType(newLessonType))
    setSelectedTab(newValue)

    // Update the URL for easily sending the link to someone else.
    const ending = newValue === "standard" ? "" : `/${newValue}`
    let url = location.pathname
    availableLessonTypes.forEach((lessonType) => {
      url = url.replace(`/${lessonType.name}`, "")
    })
    history.push(`${url}${ending}`)
  }

  return (
    <>
      <AppBar position="static" color="inherit" elevation={1}>
        <Tabs
          value={selectedTab}
          indicatorColor="primary"
          textColor="primary"
          onChange={handleTabChange}
          scrollButtons={false}
          variant="scrollable"
        >
          {availableLessonTypes.map((lessonType) => (
            <Tab label={lessonType.label} value={lessonType.name} />
          ))}
        </Tabs>
      </AppBar>
      <Paper
        role="tabpanel"
        data-cy="availability-picker"
        style={{
          borderTopLeftRadius: 0,
          borderTopRightRadius: 0,
          padding: 20,
          boxShadow:
            "0px 1px 2px 0px rgba(0,0,0,0.2), 0px 2px 2px 0px rgba(0,0,0,0.14), 0px 3px 1px -2px rgba(0,0,0,0.12)",
          borderTop: "1px solid #e6e6e6",
          minHeight: 400,
        }}
      >
        {profile.isAdmin ? (
          <Paper
            sx={{
              background: "rgb(245, 245, 245)",
              p: 2,
              mb: 2,
            }}
          >
            <h3 style={{ marginTop: 0 }}>Admin Area</h3>
            <CustomTransientSlot />
            <FormControlLabel
              label="Include Private Avails"
              control={
                <Checkbox
                  onChange={(e) =>
                    dispatch(setIncludePrivate(e.target.checked))
                  }
                  checked={includePrivate}
                />
              }
            />
          </Paper>
        ) : null}
        <PickerContent instructorId={instructorId} />
        <SpotAlreadyTaken />
      </Paper>
    </>
  )
}

export default AvailabilityPicker
